/**
 * Command Palette (paquete AD) — atajo Cmd+K / Ctrl+K global.
 *
 * Tipo Linear / Notion / Raycast: una sola caja de búsqueda que dispara
 * navegación, acciones y búsqueda de entidades sin tener que clickear por
 * el sidebar. Pensado para power-users (administradores y QA) que conocen
 * los IDs (OP-12345, SN-..., R-...).
 *
 * Secciones:
 *   1. Acciones rápidas: Sincronizar ERP/SNT · Cerrar sesión · Cambiar GLN
 *   2. Navegación: todas las rutas operativas + de seguridad (RBAC-filtered)
 *   3. Series: matching por id (case-insensitive substring)
 *   4. Remitos: matching por numero
 *   5. Operaciones: matching por id (OP-XXXXX) o codigoTransaccion
 *
 * El input está montado siempre; cmdk se encarga del fuzzy matching y la
 * navegación con keyboard. Limitamos los resultados (top 5 por sección)
 * para que la palette no se vuelva pesada con bases grandes.
 *
 * Atajo: Cmd+K (Mac) / Ctrl+K (Windows/Linux). Esc para cerrar.
 *
 * No hay E2E directo todavía — el shortcut requiere keyDown global que es
 * propenso a flakes. Lo testeamos manualmente y dejamos hook para test si
 * surge regresión.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/notify';
import {
  LayoutDashboard,
  Truck,
  Inbox,
  Package,
  GitBranch,
  ListTree,
  ScanSearch,
  FileText,
  Database,
  Shield,
  ShieldCheck,
  Activity,
  LogOut,
  RefreshCw,
  Search,
  Hash,
  FileCode,
} from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useSessionStore } from '@/stores/session';
import {
  useSeries,
  useRemitos,
  useOperaciones,
  useUbicaciones,
  useSincronizarMutation,
} from '@/api/queries';
import { puedeActuar } from '@/domain/rbac';
import { performLogout } from '@/features/auth/logout-flow';
import { fmtRemitoNumero } from '@/lib/format';

/** Cuántos items mostrar por sección antes de "and X more…". */
const MAX_POR_SECCION = 5;

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');

  const usuario = useSessionStore((s) => s.usuario);
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);
  const { data: ubicaciones = [] } = useUbicaciones();
  const { data: series = [] } = useSeries();
  const { data: remitos = [] } = useRemitos();
  const { data: operaciones = [] } = useOperaciones();
  const sincronizar = useSincronizarMutation();

  // Helper para navegar + cerrar
  const go = useCallback(
    (path: string) => {
      onOpenChange(false);
      setQuery('');
      void navigate(path);
    },
    [navigate, onOpenChange],
  );

  // Resetear query al cerrar
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const q = query.trim().toLowerCase();

  // Series matching
  const seriesMatch = useMemo(() => {
    if (q.length < 2) return [];
    return series
      .filter((s) => s.id.toLowerCase().includes(q))
      .slice(0, MAX_POR_SECCION);
  }, [series, q]);

  // Remitos matching
  const remitosMatch = useMemo(() => {
    if (q.length < 2) return [];
    return remitos
      .filter((r) => r.numero.toLowerCase().includes(q))
      .slice(0, MAX_POR_SECCION);
  }, [remitos, q]);

  // Operaciones matching (por id o codigoTransaccion)
  const opsMatch = useMemo(() => {
    if (q.length < 2) return [];
    return operaciones
      .filter(
        (o) =>
          o.id.toLowerCase().includes(q) ||
          (o.anmat.codigoTransaccion ?? '').toLowerCase().includes(q) ||
          (o.remitoNumero ?? '').toLowerCase().includes(q),
      )
      .slice(0, MAX_POR_SECCION);
  }, [operaciones, q]);

  // Multi-GLN: ubicaciones a las que el usuario podría switchear
  const ubicacionesSwitchables = useMemo(() => {
    if (!usuario) return [];
    return ubicaciones.filter(
      (u) =>
        !u.externa &&
        u.id !== ubicacionActivaId &&
        (usuario.ubicacionesIds?.includes(u.id) ?? false),
    );
  }, [ubicaciones, usuario, ubicacionActivaId]);

  const handleSincronizar = () => {
    if (!ubicacionActivaId) return;
    onOpenChange(false);
    sincronizar.mutate(
      { ubicacionId: ubicacionActivaId, usuarioId: usuario?.id },
      {
        onSuccess: (data) => {
          const total = data.remitosPendientes + data.recepcionesPendientes;
          toast.success(
            total === 0
              ? 'Sincronización OK · sin nuevos pendientes'
              : `Sincronización OK · ${data.remitosPendientes} ERP / ${data.recepcionesPendientes} SNT pendientes`,
          );
        },
        onError: (err) => toast.error(`Error al sincronizar: ${err.message}`),
      },
    );
  };

  const handleLogout = () => {
    onOpenChange(false);
    void performLogout({ navigate, queryClient });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="overflow-hidden p-0 sm:max-w-xl"
        // Removemos el close button default — cmdk maneja el Esc
        aria-label="Paleta de comandos"
      >
        <Command
          className="flex h-[480px] flex-col"
          // cmdk se encarga del fuzzy filtering por `value` de cada Item.
          // Series/Remitos/Ops también filtramos previo (top 5 por sección)
          // para no explotar con miles de items en el DOM.
          loop
        >
          <div className="flex items-center border-b border-gray-200 px-4">
            <Search className="mr-2 h-4 w-4 shrink-0 text-gray-500" />
            <Command.Input
              value={query}
              onValueChange={setQuery}
              placeholder="Buscar vistas, series, remitos, operaciones…"
              className="flex h-12 w-full bg-transparent text-sm outline-none placeholder:text-gray-500"
              autoFocus
            />
            <kbd className="ml-2 hidden rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] font-mono text-gray-600 sm:inline">
              Esc
            </kbd>
          </div>

          <Command.List className="flex-1 overflow-y-auto p-2">
            <Command.Empty className="py-8 text-center text-sm text-gray-600">
              {q.length < 2 && q.length > 0
                ? 'Escribí al menos 2 caracteres para buscar series, remitos u operaciones'
                : 'Sin resultados. Probá con un id de serie, número de remito o OP-XXXXX.'}
            </Command.Empty>

            {/* Acciones rápidas — siempre visibles */}
            <Section heading="Acciones">
              <Item
                onSelect={handleSincronizar}
                icon={<RefreshCw className="h-4 w-4" />}
                label="Sincronizar ERP / SNT"
                hint="Refrescar pendientes y recepciones"
              />
              {ubicacionesSwitchables.length > 0 && (
                <Item
                  onSelect={() => go('/dashboard')}
                  icon={<Database className="h-4 w-4" />}
                  label="Cambiar GLN…"
                  hint={`${ubicacionesSwitchables.length} ubicaciones disponibles`}
                />
              )}
              <Item
                onSelect={handleLogout}
                icon={<LogOut className="h-4 w-4" />}
                label="Cerrar sesión"
                hint="Logout del usuario activo"
                destructive
              />
            </Section>

            {/* Navegación principal */}
            <Section heading="Ir a">
              <Item onSelect={() => go('/dashboard')} icon={<LayoutDashboard className="h-4 w-4" />} label="Dashboard" />
              <Item onSelect={() => go('/despacho')} icon={<Truck className="h-4 w-4" />} label="Despacho" />
              <Item onSelect={() => go('/recepcion')} icon={<Inbox className="h-4 w-4" />} label="Recepción" />
              <Item onSelect={() => go('/series')} icon={<Package className="h-4 w-4" />} label="Series" />
              <Item
                onSelect={() => go('/trazabilidad')}
                icon={<GitBranch className="h-4 w-4" />}
                label="Trazabilidad"
              />
              <Item onSelect={() => go('/anmat')} icon={<ListTree className="h-4 w-4" />} label="Log ANMAT" />
              <Item
                onSelect={() => go('/consulta-gln')}
                icon={<ScanSearch className="h-4 w-4" />}
                label="Consulta GLN ANMAT"
              />
              <Item
                onSelect={() => go('/reportes/movimientos')}
                icon={<FileText className="h-4 w-4" />}
                label="Reportes · Movimientos"
              />
              <Item
                onSelect={() => go('/reportes/cruce')}
                icon={<FileText className="h-4 w-4" />}
                label="Reportes · Cruce ERP ↔ SNT"
              />
              <Item
                onSelect={() => go('/maestros/productos')}
                icon={<Database className="h-4 w-4" />}
                label="Maestros · Productos"
              />
              {puedeActuar(usuario, 'ver_seguridad') && (
                <>
                  <Item
                    onSelect={() => go('/seguridad/usuarios')}
                    icon={<Shield className="h-4 w-4" />}
                    label="Seguridad · Usuarios"
                  />
                  <Item
                    onSelect={() => go('/seguridad/roles')}
                    icon={<Shield className="h-4 w-4" />}
                    label="Seguridad · Roles y permisos"
                  />
                </>
              )}
              {puedeActuar(usuario, 'ver_audit_trail') && (
                <>
                  <Item
                    onSelect={() => go('/audit')}
                    icon={<ShieldCheck className="h-4 w-4" />}
                    label="Audit Trail"
                  />
                  <Item
                    onSelect={() => go('/apilog')}
                    icon={<Activity className="h-4 w-4" />}
                    label="Logs API"
                  />
                  <Item
                    onSelect={() => go('/handoff')}
                    icon={<FileCode className="h-4 w-4" />}
                    label="Handoff técnico"
                  />
                </>
              )}
              {/* Manual oculto por pedido — la ruta /manual sigue existiendo (accesible por URL directa). */}
            </Section>

            {/* Resultados de entidades — solo si query >= 2 chars */}
            {seriesMatch.length > 0 && (
              <Section heading={`Series (${seriesMatch.length})`}>
                {seriesMatch.map((s) => (
                  <Item
                    key={s.id}
                    onSelect={() => go(`/trazabilidad?serie=${encodeURIComponent(s.id)}`)}
                    icon={<Hash className="h-4 w-4" />}
                    label={s.id}
                    hint={`GTIN ${s.gtin} · Lote ${s.lote} · ${s.tenencia}`}
                  />
                ))}
              </Section>
            )}

            {remitosMatch.length > 0 && (
              <Section heading={`Remitos (${remitosMatch.length})`}>
                {remitosMatch.map((r) => (
                  <Item
                    key={r.numero}
                    onSelect={() => go(`/despacho`)}
                    icon={<FileText className="h-4 w-4" />}
                    label={fmtRemitoNumero(r.numero)}
                    hint={`${r.destinoTipo} · ${r.items.length} items · ${r.estadoEnPlataforma}`}
                  />
                ))}
              </Section>
            )}

            {opsMatch.length > 0 && (
              <Section heading={`Operaciones (${opsMatch.length})`}>
                {opsMatch.map((o) => (
                  <Item
                    key={o.id}
                    onSelect={() => go(`/operaciones/${o.id}`)}
                    icon={<Truck className="h-4 w-4" />}
                    label={o.id}
                    hint={`${o.tipo.replace('despacho_', '')} · ${o.seriesIds.length} series · ${o.anmat.estado}`}
                  />
                ))}
              </Section>
            )}
          </Command.List>

          <div className="flex items-center justify-between border-t border-gray-200 px-3 py-2 text-[11px] text-gray-600">
            <div className="flex items-center gap-3">
              <span>
                <kbd className="rounded border border-gray-200 bg-gray-50 px-1 py-0.5 font-mono">↑</kbd>{' '}
                <kbd className="rounded border border-gray-200 bg-gray-50 px-1 py-0.5 font-mono">↓</kbd>{' '}
                navegar
              </span>
              <span>
                <kbd className="rounded border border-gray-200 bg-gray-50 px-1 py-0.5 font-mono">↵</kbd>{' '}
                seleccionar
              </span>
            </div>
            <span className="font-mono">Cmd+K</span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

/** Sección con heading. cmdk maneja la group semantics. */
function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <Command.Group
      heading={heading}
      className="mb-1 px-1 pt-1 text-[11px] font-bold uppercase tracking-wider text-gray-500 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1"
    >
      {children}
    </Command.Group>
  );
}

interface ItemProps {
  onSelect: () => void;
  icon?: React.ReactNode;
  label: string;
  hint?: string;
  destructive?: boolean;
}

function Item({ onSelect, icon, label, hint, destructive }: ItemProps) {
  return (
    <Command.Item
      onSelect={onSelect}
      // Value es lo que cmdk usa para fuzzy match
      value={`${label} ${hint ?? ''}`}
      className={`flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm aria-selected:bg-everex-azul/10 aria-selected:text-everex-azul ${destructive ? 'text-red-600 aria-selected:bg-red-50 aria-selected:text-red-700' : 'text-gray-700'}`}
    >
      {icon && <span className="shrink-0 text-gray-600">{icon}</span>}
      <span className="flex-1 truncate">{label}</span>
      {hint && <span className="truncate text-[11px] text-gray-500">{hint}</span>}
    </Command.Item>
  );
}
